/**
 * Streak Service
 * Central source of truth for streak math and freeze logic.
 */

export const getFreezeCap = () => {
  return 2;
};

export const normalizeDate = (date = new Date()) => {
  if (typeof date === 'string') return date.split('T')[0];
  return date.toISOString().split('T')[0];
};

export const shouldResetWorkout = (lastActiveDate, today) => {
  if (!lastActiveDate) return true;
  return lastActiveDate !== today;
};

/**
 * Evaluates missed days and decides whether to consume freezes or reset streak.
 * 
 * @param {Object} user - User object containing streak, streak_freezes, last_active_date
 * @param {string} today - Normalized today's date (YYYY-MM-DD)
 * @returns {Object} { updatedStreak, updatedFreezes, freezeUsed, streakBroken, missedDays }
 */
export const evaluateMissedDays = (user, today) => {
  // Support both Supabase naming and local state naming
  const lastActive = user.last_active_date || user.lastPlayedDate;
  
  // NORMALIZE INPUTS: Handle null, undefined, and non-numeric values safely
  const currentStreak = Number(user.streak ?? user.currentStreak ?? 0) || 0;
  const currentFreezes = Number(user.streak_freezes ?? user.streakFreezes ?? 0) || 0;
  
  if (!lastActive) {
    return {
      updatedStreak: currentStreak,
      updatedFreezes: currentFreezes,
      freezeUsed: false,
      streakBroken: false,
      missedDays: 0
    };
  }

  const lastActiveDate = new Date(lastActive);
  const todayDate = new Date(today);
  
  // Calculate difference in days using UTC timestamps to avoid ALL timezone/local-drift issues.
  const t1 = Date.UTC(lastActiveDate.getUTCFullYear(), lastActiveDate.getUTCMonth(), lastActiveDate.getUTCDate());
  const t2 = Date.UTC(todayDate.getUTCFullYear(), todayDate.getUTCMonth(), todayDate.getUTCDate());
  
  const diffTime = Math.abs(t2 - t1);
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  // Formula: missedDays = max(0, diffDays - 1)
  let missedDaysCount = Math.max(0, diffDays - 1);
  
  if (missedDaysCount === 0) {
    return {
      updatedStreak: currentStreak,
      updatedFreezes: currentFreezes,
      freezeUsed: false,
      streakBroken: false,
      missedDays: 0
    };
  }

  // Missed days detected
  let remainingStreak = currentStreak;
  let remainingFreezes = currentFreezes;
  let freezeUsedCount = 0;

  if (currentStreak > 0) {
    // Attempt to use freezes to cover missed days
    while (missedDaysCount > 0 && remainingFreezes > 0) {
      missedDaysCount--;
      remainingFreezes--;
      freezeUsedCount++;
    }

    if (missedDaysCount > 0) {
      remainingStreak = 0;
    }
  } else {
    remainingStreak = 0;
  }

  return {
    updatedStreak: remainingStreak,
    updatedFreezes: remainingFreezes,
    freezeUsed: freezeUsedCount > 0,
    streakBroken: remainingStreak === 0 && currentStreak > 0,
    missedDays: diffDays - 1
  };
};

/**
 * Calculates milestone rewards (+1 freeze every 5 days)
 */
export const calculateMilestoneReward = (oldStreak, newStreak, currentFreezes) => {
  const cap = getFreezeCap();
  const normalizedFreezes = Number(currentFreezes) || 0;
  if (normalizedFreezes >= cap) return 0;
  
  if (newStreak > oldStreak && newStreak % 5 === 0 && newStreak > 0) {
    return 1;
  }
  return 0;
};

/**
 * Checks if a user should receive their weekly Monday bonus freeze.
 * Gating is handled at the app level (only paid users reach this logic).
 */
export const shouldGrantMondayBonus = (user, today) => {
  const dateObj = new Date(today);
  const isMonday = dateObj.getUTCDay() === 1;
  
  if (!isMonday) return false;
  
  const lastBonusDate = user.last_monday_freeze_date || user.lastMondayFreezeDate;
  return lastBonusDate !== today;
};

/**
 * Returns the UTC date string for the day before the given date.
 */
export const getYesterday = (today) => {
  const d = new Date(today);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().split('T')[0];
};
